import * as React from "react";
import {useCallback, useMemo} from "react";
import {useEditor, EditorContent} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {useIntl} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBold,
    faItalic,
    faListUl,
    faListOl,
    faLink,
    faLinkSlash,
} from "@fortawesome/pro-regular-svg-icons";
import TemplateVariablePicker from "../form/TemplateVariablePicker";
import {BlockType} from "../../types/emailBlocks";

interface InlineBlockEditorProps {
    value: string;
    onChange: (value: string) => void;
    blockType: BlockType;
    placeholder?: string;
    autoFocus?: boolean;
}

// Configure extensions at module level
const configuredStarterKit = StarterKit.configure({
    heading: false, // No heading in inline editor - block type determines heading
    codeBlock: false,
    code: false,
});

const configuredLink = Link.configure({
    openOnClick: false,
    autolink: true,
    HTMLAttributes: {
        rel: "noopener noreferrer",
    },
});

interface ToolbarButtonProps {
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
    onClick,
    isActive = false,
    disabled = false,
    title,
    children,
}) => {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={`p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                isActive ? "bg-gray-200 text-red-600" : "text-gray-600"
            }`}
        >
            {children}
        </button>
    );
};

const InlineBlockEditor: React.FC<InlineBlockEditorProps> = ({
    value,
    onChange,
    blockType,
    placeholder = "",
    autoFocus = false,
}) => {
    const intl = useIntl();
    const [showLinkInput, setShowLinkInput] = React.useState(false);
    const [linkUrl, setLinkUrl] = React.useState("");

    // For heading blocks, we don't want HTML tags, just plain text
    const isHeading = blockType === 'heading';

    const extensions = useMemo(() => [
        configuredStarterKit,
        configuredLink,
        Placeholder.configure({
            placeholder: placeholder,
        }),
    ], [placeholder]);

    const editor = useEditor({
        extensions,
        content: value || "",
        autofocus: autoFocus ? "end" : false,
        onUpdate: ({editor}) => {
            if (isHeading) {
                // For headings, extract text content only
                onChange(editor.getText());
            } else {
                onChange(editor.getHTML());
            }
        },
        editorProps: {
            attributes: {
                class: isHeading
                    ? "prose prose-sm max-w-none focus:outline-hidden min-h-[40px] px-2 py-1"
                    : "prose prose-sm max-w-none focus:outline-hidden min-h-[100px] px-2 py-1",
            },
        },
        immediatelyRender: false,
    });

    const handleInsertVariable = useCallback((variable: string) => {
        if (editor) {
            editor.chain().focus().insertContent(variable).run();
        }
    }, [editor]);

    const handleSetLink = useCallback(() => {
        if (!editor) return;

        if (linkUrl === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
        } else {
            editor.chain().focus().extendMarkRange("link").setLink({href: linkUrl}).run();
        }
        setShowLinkInput(false);
        setLinkUrl("");
    }, [editor, linkUrl]);

    const handleOpenLinkInput = useCallback(() => {
        if (!editor) return;
        const previousUrl = editor.getAttributes("link").href || "";
        setLinkUrl(previousUrl);
        setShowLinkInput(true);
    }, [editor]);

    const handleRemoveLink = useCallback(() => {
        if (!editor) return;
        editor.chain().focus().unsetLink().run();
    }, [editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="inline-block-editor border border-gray-200 rounded-sm bg-white">
            {/* Toolbar - simpler for headings */}
            <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-2 py-1">
                <div className="flex items-center gap-1">
                    {/* Bold */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        isActive={editor.isActive("bold")}
                        title={intl.formatMessage({id: "richTextEditor.bold"})}
                    >
                        <FontAwesomeIcon icon={faBold} className="w-3 h-3"/>
                    </ToolbarButton>

                    {/* Italic */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        isActive={editor.isActive("italic")}
                        title={intl.formatMessage({id: "richTextEditor.italic"})}
                    >
                        <FontAwesomeIcon icon={faItalic} className="w-3 h-3"/>
                    </ToolbarButton>

                    {/* Lists only for paragraphs */}
                    {!isHeading && (
                        <>
                            <div className="w-px h-4 bg-gray-300 mx-1"/>
                            <ToolbarButton
                                onClick={() => editor.chain().focus().toggleBulletList().run()}
                                isActive={editor.isActive("bulletList")}
                                title={intl.formatMessage({id: "richTextEditor.bulletList"})}
                            >
                                <FontAwesomeIcon icon={faListUl} className="w-3 h-3"/>
                            </ToolbarButton>
                            <ToolbarButton
                                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                                isActive={editor.isActive("orderedList")}
                                title={intl.formatMessage({id: "richTextEditor.orderedList"})}
                            >
                                <FontAwesomeIcon icon={faListOl} className="w-3 h-3"/>
                            </ToolbarButton>
                        </>
                    )}

                    <div className="w-px h-4 bg-gray-300 mx-1"/>

                    {/* Link */}
                    {showLinkInput ? (
                        <div className="flex items-center gap-1">
                            <input
                                type="url"
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                placeholder="https://..."
                                className="h-6 px-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-red-500 w-32"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleSetLink();
                                    }
                                    if (e.key === "Escape") {
                                        setShowLinkInput(false);
                                        setLinkUrl("");
                                    }
                                }}
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={handleSetLink}
                                className="px-1.5 py-0.5 text-xs bg-red-600 text-white rounded-sm hover:bg-red-700"
                            >
                                OK
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowLinkInput(false);
                                    setLinkUrl("");
                                }}
                                className="px-1 text-xs text-gray-500 hover:text-gray-700"
                            >
                                x
                            </button>
                        </div>
                    ) : (
                        <>
                            <ToolbarButton
                                onClick={handleOpenLinkInput}
                                isActive={editor.isActive("link")}
                                title={intl.formatMessage({id: "richTextEditor.addLink"})}
                            >
                                <FontAwesomeIcon icon={faLink} className="w-3 h-3"/>
                            </ToolbarButton>

                            {editor.isActive("link") && (
                                <ToolbarButton
                                    onClick={handleRemoveLink}
                                    title={intl.formatMessage({id: "richTextEditor.removeLink"})}
                                >
                                    <FontAwesomeIcon icon={faLinkSlash} className="w-3 h-3"/>
                                </ToolbarButton>
                            )}
                        </>
                    )}
                </div>

                <TemplateVariablePicker onInsert={handleInsertVariable}/>
            </div>

            <EditorContent editor={editor}/>
        </div>
    );
};

export default InlineBlockEditor;
